/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useState } from 'react'
import { Spin, Typography } from 'antd'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AuthCard } from '../../components/Auth/AuthCard'
import { AuthForm } from '../../components/Auth/AuthForm'
import type { AuthFormData } from '../../types/auth'
import { useApi } from '../../services/ApiContext'
import { GoogleReCaptcha } from 'react-google-recaptcha-v3'
import { FeathersJSError } from '../../services/types'
import { fireGoogleAdsSignupConversion, pushGtmEvent } from '../../utils/gtm'
import { marketingSlogans, usePageTitle } from '../../utils/pageTitle'
import { useCompanyStore } from '../../store/company/companyStore'

const { Text } = Typography

export const SignupPage: React.FC = () => {
  usePageTitle(marketingSlogans[0])
  const api = useApi()
  const user = useCompanyStore((state) => state.user)

  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [captcha, setCaptcha] = React.useState<string | null>(null)
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false)
  const [searchParams] = useSearchParams()
  const redirect = searchParams.get('redirect')

  const onVerify = useCallback((token: string | null) => {
    setCaptcha(token)
  }, [])

  const navigate = useNavigate()

  const handleSubmit = async (data: AuthFormData) => {
    setLoading(true)
    if (!captcha) {
      setLoading(false)
      return
    }
    try {
      let success = false

      if (!user) {
        success = await api.signup({ ...data, captcha })
      } else {
        success = await api.convertDemoToUser({ ...data, captcha })
      }
      if (success) {
        // pushGtmEvent('signup')
        fireGoogleAdsSignupConversion()

        if (redirect) {
          navigate(redirect)
        } else {
          navigate('/app/documents?new=true')
        }
      }
    } catch (error) {
      console.log(error)
      if ((error as FeathersJSError).className === 'conflict') {
        setError('Email address already in use, please log in instead.')
      } else if ((error as FeathersJSError).className) {
        setError((error as FeathersJSError).message)
      } else {
        setError(`We couldn't sign you up, please try again.`)
      }
    }
    setRefreshReCaptcha((r: boolean) => !r)
    setLoading(false)
  }
  return (
    <AuthCard>
      <Spin spinning={loading} tip="Signing you up...">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-2">Create Your Account</h1>
          <Text type="secondary">
            Get started saving time with SprintSpec for free
          </Text>
        </div>

        <AuthForm type="signup" onSubmit={handleSubmit} />
        {error && (
          <Text type="danger" className="block mb-4 text-center">
            {error}
          </Text>
        )}

        {/* <AuthSocial /> */}

        <div className="text-center mt-8">
          <Text type="secondary">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:text-primary-light">
              Sign in
            </Link>
          </Text>
        </div>

        <GoogleReCaptcha
          refreshReCaptcha={refreshReCaptcha}
          onVerify={onVerify}
        />
        <div className="text-center mt-4">
          <Text type="secondary" className="text-xs">
            By signing up, you agree to our{' '}
            <Link
              to="/terms-of-service"
              className="text-gray-600 hover:text-gray-900"
            >
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              to="/privacy-policy"
              className="text-gray-600 hover:text-gray-900"
            >
              Privacy Policy
            </Link>
          </Text>
        </div>
      </Spin>
    </AuthCard>
  )
}
